import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'

/** Роль сотрудника бекофиса, которой вправе управлять владелец/супер-админ. */
type ManageableRole = 'admin' | 'viewer' | 'accountant'

/** Роль в списке: бэкенд отдаёт всех сотрудников клуба, в т.ч. владельца. */
type StaffMemberRole = ManageableRole | 'owner'

const ROLES: ManageableRole[] = ['admin', 'viewer', 'accountant']

/** Управлять (изменять/удалять) можно только этими ролями — не владельцем. */
const MANAGEABLE_ROLES: readonly StaffMemberRole[] = ['admin', 'viewer', 'accountant']

const ROLE_LABEL: Record<StaffMemberRole, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  viewer: 'Наблюдатель',
  accountant: 'Бухгалтер'
}

/** Сотрудник бекофиса (вход email/пароль). Бэкенд отдаёт ровно эти поля. */
interface StaffMember {
  id: string
  email: string
  name: string
  role: StaffMemberRole
  /** Заблокирован: вход закрыт, аккаунт и история сохранены. null = активен. */
  disabledAt: number | null
  createdAt: number
}

export function Team(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Команда"
        title="Сотрудники БО"
        lead="Учётные записи бекофиса — вход по email и паролю, с ролями «Администратор» (полный доступ к управлению) или «Наблюдатель» (только просмотр). Это НЕ PIN-пользователи терминала из раздела «Пользователи»: те входят по коду в кассовое приложение."
      />
      <ClubGate>
        <TeamTable />
      </ClubGate>
    </>
  )
}

function TeamTable(): React.JSX.Element {
  // Для super_admin — выбранный в шапке клуб как ?clubId; для владельца — undefined
  // (клуб выводится из токена на бэкенде).
  const query = useConfigQuery()
  const list = useFetch<StaffMember[]>('/v1/staff', query, [query?.clubId])
  const [editing, setEditing] = useState<StaffMember | 'new' | null>(null)
  const [deleting, setDeleting] = useState<StaffMember | null>(null)
  const del = useMutate()
  const toggle = useMutate()

  const rows = list.data ?? []

  /** Блокировка/разблокировка входа без удаления аккаунта. */
  const onToggleDisabled = async (s: StaffMember): Promise<void> => {
    const ok = await toggle.mutate(() =>
      api.patch(`/v1/staff/${s.id}`, { disabled: !s.disabledAt }, { query })
    )
    if (ok) list.reload()
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/staff/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} сотрудник(-ов)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый сотрудник
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Сотрудники бекофиса ещё не добавлены."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Email</th>
                <th>Имя</th>
                <th>Роль</th>
                <th>Статус</th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} style={s.disabledAt ? { opacity: 0.6 } : undefined}>
                  <td>{s.email}</td>
                  <td>{s.name}</td>
                  <td>{ROLE_LABEL[s.role]}</td>
                  <td>
                    {s.disabledAt ? (
                      <span style={{ color: 'var(--amber)' }}>заблокирован</span>
                    ) : (
                      <span style={{ color: 'var(--green)' }}>активен</span>
                    )}
                  </td>
                  <td>
                    {/* Владельца изменить/удалить нельзя (бэкенд вернёт 403) — прячем действия. */}
                    {MANAGEABLE_ROLES.includes(s.role) && (
                      <div className="row-actions">
                        <IconButton title="Изменить" onClick={() => setEditing(s)}>
                          {Icon.edit}
                        </IconButton>
                        {/* Блокировка вместо удаления: сотрудник ушёл в отпуск или
                            уволился, а история его действий должна остаться. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={toggle.saving}
                          onClick={() => void onToggleDisabled(s)}
                        >
                          {s.disabledAt ? 'Вернуть доступ' : 'Заблокировать'}
                        </Button>
                        <IconButton danger title="Удалить" onClick={() => setDeleting(s)}>
                          {Icon.trash}
                        </IconButton>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <StaffForm
          staff={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить сотрудника"
          message={`Удалить учётную запись «${deleting.email}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function StaffForm({
  staff,
  query,
  onClose,
  onSaved
}: {
  staff: StaffMember | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = staff !== null
  const [email, setEmail] = useState(staff?.email ?? '')
  const [name, setName] = useState(staff?.name ?? '')
  // Форму открываем только для admin/viewer; владельца сюда не пускаем (страховка типов).
  const [role, setRole] = useState<ManageableRole>(
    staff && staff.role !== 'owner' ? staff.role : 'viewer'
  )
  const [password, setPassword] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    if (!isEdit && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail))
      return setValidation('Укажите корректный email.')
    if (!trimmedName) return setValidation('Укажите имя сотрудника.')
    // Пароль обязателен при создании; при редактировании — только если меняют.
    if (!isEdit && password.length < 6) return setValidation('Пароль не короче 6 символов.')
    if (isEdit && password && password.length < 6)
      return setValidation('Пароль не короче 6 символов.')
    setValidation(null)

    const ok = await save.mutate(() => {
      if (isEdit) {
        // Отправляем пароль только если его задали (иначе не меняем).
        const body: Record<string, unknown> = { name: trimmedName, role }
        if (password) body.password = password
        return api.patch(`/v1/staff/${staff.id}`, body, { query })
      }
      return api.post(
        '/v1/staff',
        {
          email: trimmedEmail,
          name: trimmedName,
          role,
          password
        },
        { query }
      )
    })
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить сотрудника' : 'Новый сотрудник'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="staff-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="staff-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Email" htmlFor="s-email" full>
            <Input
              id="s-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="напр. admin@club.by"
              disabled={isEdit}
              autoFocus={!isEdit}
            />
            {isEdit && <span className="field__hint">Email менять нельзя.</span>}
          </Field>
          <Field label="Имя" htmlFor="s-name" full>
            <Input
              id="s-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Иван Петров"
              autoFocus={isEdit}
            />
          </Field>
          <Field label="Роль" htmlFor="s-role">
            <Select
              id="s-role"
              value={role}
              onChange={(e) => setRole(e.target.value as ManageableRole)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Пароль" htmlFor="s-password">
            <Input
              id="s-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isEdit ? 'не менять' : 'не короче 6 символов'}
            />
            {isEdit && (
              <span className="field__hint">Оставьте пустым, чтобы не менять пароль.</span>
            )}
          </Field>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}
